import React, { useEffect, useMemo } from "react";
import { DatePicker, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import moment from "moment-timezone";
import { calculateDispatchDate, dayjsFormat } from "../../../../helpers/utils";
import { getBusinessTimeZone } from "../../../../helpers/moment";
import { svg } from "../../../../Assets/svg";
import {
  DeliveryStatusArray,
  PoductStartEditArray,
  InstallationStatusArray,
} from "../orderTypes";

dayjs.extend(isoWeek);

interface Props {
  values: any;
  setFieldValue: (key: string, value: any) => void;
  errors: any;
  touched: any;
  orderDetails: any;
  allowanceDays?: number;
}

const parseDateToMoment = (dateVal: any, timeZone: string) => {
  if (!dateVal) return null;
  if (typeof dateVal?.toDate === "function") {
    return moment(dateVal.toDate()).tz(timeZone);
  }
  if (dateVal instanceof Date) {
    return moment(dateVal).tz(timeZone);
  }
  return moment(dateVal).tz(timeZone);
};

const PlanningHeaderForm: React.FC<Props> = ({
  values,
  setFieldValue,
  errors,
  touched,
  orderDetails,
  allowanceDays,
}) => {
  const tz = useMemo(() => getBusinessTimeZone(), []);

  const effectiveAllowance = useMemo(() => {
    if (allowanceDays !== undefined && allowanceDays !== null) {
      return Number(allowanceDays) || 0;
    }
    if (
      values?.dispatch_to_delivery_allowance !== undefined &&
      values?.dispatch_to_delivery_allowance !== null
    ) {
      return Number(values.dispatch_to_delivery_allowance) || 0;
    }
    if (
      orderDetails?.planning_details?.dispatch_to_delivery_allowance !== undefined &&
      orderDetails?.planning_details?.dispatch_to_delivery_allowance !== null
    ) {
      return (
        Number(orderDetails.planning_details.dispatch_to_delivery_allowance) || 0
      );
    }
    if (
      orderDetails?.delivery?.dispatch_to_delivery_allowance !== undefined &&
      orderDetails?.delivery?.dispatch_to_delivery_allowance !== null
    ) {
      return Number(orderDetails.delivery.dispatch_to_delivery_allowance) || 0;
    }
    if (
      orderDetails?.customer_snapshot?.delivery_address
        ?.dispatch_to_delivery_allowance !== undefined &&
      orderDetails?.customer_snapshot?.delivery_address
        ?.dispatch_to_delivery_allowance !== null
    ) {
      return (
        Number(
          orderDetails.customer_snapshot.delivery_address
            .dispatch_to_delivery_allowance,
        ) || 0
      );
    }
    return 0;
  }, [allowanceDays, values?.dispatch_to_delivery_allowance, orderDetails]);


  // ✅ Helper to disable dates before today
  const disabledDate = (current: Dayjs) => {
    // Can not select days before today
    return current && current < dayjs().startOf("day");
  };

  const currentWeekNumber = useMemo(() => {
    const m = moment().tz(tz);
    return m.isValid() ? m.isoWeek() : dayjs().isoWeek();
  }, [tz]);

  // Set current week as default if not selected
  useEffect(() => {
    if (!values?.production_start_date) {
      const startOfBizWeek = moment().tz(tz).startOf("isoWeek").hour(12).minute(0).second(0);
      setFieldValue("production_start_date", dayjs(startOfBizWeek.toDate()));
    }
  }, [values?.production_start_date, tz, setFieldValue]);

  const weekOptions = useMemo(() => {
    return Array.from({ length: 52 }, (_, i) => ({
      value: i + 1,
      label: `Week ${i + 1}`,
    }));
  }, []);

  const handleWeekChange = (selectedWeek: number | null | undefined) => {
    const bizTz = getBusinessTimeZone();
    if (!selectedWeek) {
      const startOfBizWeek = moment().tz(bizTz).startOf("isoWeek").hour(12).minute(0).second(0);
      setFieldValue("production_start_date", dayjs(startOfBizWeek.toDate()));
      return;
    }

    const todayInBiz = moment().tz(bizTz);
    const currentWeek = todayInBiz.isoWeek();
    const currentYear = todayInBiz.year();

    const targetYear =
      selectedWeek >= currentWeek ? currentYear : currentYear + 1;
    const targetDate = moment()
      .tz(bizTz)
      .year(targetYear)
      .isoWeek(selectedWeek)
      .startOf("isoWeek")
      .hour(12)
      .minute(0)
      .second(0);

    setFieldValue("production_start_date", dayjs(targetDate.toDate()));
  };

  const selectedWeekValue = useMemo(() => {
    if (values?.production_start_date) {
      const m = parseDateToMoment(values.production_start_date, tz);
      if (m && m.isValid()) {
        return m.isoWeek();
      }
    }
    return currentWeekNumber;
  }, [values?.production_start_date, tz, currentWeekNumber]);

  const productionSlotOptions = useMemo(() => {
    const slots: { value: string; label: string }[] = [
      { value: "FAST-TRACK", label: "FAST-TRACK" },
    ];
    for (let i = 1; i <= 50; i++) {
      const slotStr = String(i).padStart(2, "0");
      slots.push({
        value: slotStr,
        label: slotStr,
      });
    }
    return slots;
  }, []);

  const selectedSlotValue = useMemo(() => {
    if (!values?.production_slot) return undefined;
    if (values.production_slot === "FAST-TRACK") return "FAST-TRACK";
    if (values.production_slot.includes(".")) {
      return values.production_slot.split(".")[1] || values.production_slot;
    }
    return values.production_slot;
  }, [values?.production_slot]);

  return (
    <div className="row g-3 page-form">
      {/* Production Start Week */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">Production Start Week</label>
        <Select
          className="w-100"
          size="large"
          suffixIcon={svg?.selectDownArrow}
          placeholder="Select Production Start Week"
          value={selectedWeekValue}
          onChange={handleWeekChange}
          disabled={
            !PoductStartEditArray?.includes(orderDetails?.order_status)
          }
          options={weekOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <div className="text-muted fs-8 mt-1">
          Current Week: Week {currentWeekNumber}
        </div>
      </div>

      {/* Production Slot */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">Production Slot</label>
        <Select
          className="w-100"
          size="large"
          suffixIcon={svg?.selectDownArrow}
          placeholder="Select Production Slot"
          value={selectedSlotValue}
          onChange={(val) => setFieldValue("production_slot", val || null)}
          disabled={
            !PoductStartEditArray?.includes(orderDetails?.order_status)
          }
          options={productionSlotOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          allowClear
        />
        {errors?.production_slot && touched?.production_slot && (
          <div className="text-danger fs-8">{errors?.production_slot}</div>
        )}
      </div>

      {/* On-Site Delivery Date */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">On-Site Delivery Date</label>
        <DatePicker
          suffixIcon={svg?.calenderIcon}
          className="w-100"
          size="large"
          showTime={{ use12Hours: true, format: "hh:mm A" }}
          format="DD-MM-YYYY, hh:mm A"
          placeholder="Not Specified"
          value={values?.delivery_date ? dayjs(values.delivery_date) : null}
          onChange={(date) => {
            setFieldValue("delivery_date", date ? date : null);
            if (date) {
              const autoDispatch = calculateDispatchDate(
                date,
                effectiveAllowance,
              );
              setFieldValue(
                "dispatch_date",
                autoDispatch ? autoDispatch : null,
              );
            } else {
              setFieldValue("dispatch_date", null);
            }
          }}
          disabled={
            !DeliveryStatusArray?.includes(orderDetails?.order_status)
          }
          disabledDate={disabledDate}
        />
        {errors?.delivery_date && touched?.delivery_date && (
          <div className="text-danger fs-8">{errors?.delivery_date}</div>
        )}
      </div>

      {/* Dispatch Date */}
      <div className="col-lg-3 col-md-6 col-sm-6">
        <label className="custom-label mb-1 ff">Dispatch Date</label>
        <DatePicker
          suffixIcon={svg?.calenderIcon}
          className="w-100"
          size="large"
          showTime={{ use12Hours: true, format: "hh:mm A" }}
          format="DD-MM-YYYY, hh:mm A"
          placeholder="Not Specified"
          value={values?.dispatch_date ? dayjs(values.dispatch_date) : null}
          onChange={(date) =>
            setFieldValue("dispatch_date", date ? date : null)
          }
          disabled={
            !DeliveryStatusArray?.includes(orderDetails?.order_status)
          }
          disabledDate={disabledDate}
        />
        {errors?.dispatch_date && touched?.dispatch_date && (
          <div className="text-danger fs-8">{errors?.dispatch_date}</div>
        )}
      </div>

      {/* Installation Date */}
      {values?.installation_planning ? (
        <div className="col-lg-3 col-md-6 col-sm-6">
          <label className="custom-label mb-1 ff">Installation Date</label>
          <DatePicker
            suffixIcon={svg?.calenderIcon}
            className="w-100"
            size="large"
            format={dayjsFormat}
            placeholder="Select Installation Date"
            value={
              values?.installation_date ? dayjs(values.installation_date) : null
            }
            onChange={(date) =>
              setFieldValue("installation_date", date ? date : null)
            }
            disabled={
              !values.delivery_date ||
              !InstallationStatusArray?.includes(orderDetails?.order_status)
            }
            disabledDate={disabledDate}
          />
          {errors?.installation_date && touched?.installation_date && (
            <div className="text-danger fs-8">{errors?.installation_date}</div>
          )}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default PlanningHeaderForm;
